import {
  getClientLanguages,
  getMatchLanguages,
  orderByLanguage
} from "./chunk-2UKM3YYQ.js";
import {
  getLogger
} from "./chunk-LFIBM62H.js";

// src/adapter/htmlmedia-subtitles.ts
import cuid from "cuid";
var Mode = {
  Disabled: "disabled",
  Hidden: "hidden",
  Showing: "showing"
};
var logger = getLogger("HTMLMediaSubtitles");
var HTMLMediaSubtitles = class {
  element;
  eventEmitter;
  activeMode = Mode.Showing;
  previousNumTracks = 0;
  currentActiveLabel;
  allowAutomaticSelection = true;
  subtitles;
  addTrackGroupId;
  constructor(element, eventEmitter, mediaSubtitles, displaySubtitles) {
    this.element = element;
    this.eventEmitter = eventEmitter;
    this.subtitles = mediaSubtitles ?? [];
    this.handleTrackListChanged = this.handleTrackListChanged.bind(this);
    element.textTracks.addEventListener("addtrack", this.handleTrackListChanged);
    element.textTracks.addEventListener("change", this.handleTrackListChanged);
    element.textTracks.addEventListener("removetrack", this.handleTrackListChanged);
    element.addEventListener("loadedmetadata", this.handleTrackListChanged);
    element.addEventListener("loadeddata", this.handleTrackListChanged);
    this.displaySubtitles(displaySubtitles ?? true);
    if (mediaSubtitles === void 0 || mediaSubtitles.length === 0) {
      logger.debug("No tracks to wait for. Emitting event.");
      this.emitTrackChange(false);
    } else if (element.networkState >= element.HAVE_METADATA) {
      this.handleTrackListChanged(new Event("lateinit"));
    }
  }
  destroy() {
    this.element?.textTracks.removeEventListener("addtrack", this.handleTrackListChanged);
    this.element?.textTracks.removeEventListener("change", this.handleTrackListChanged);
    this.element?.textTracks.removeEventListener("removetrack", this.handleTrackListChanged);
    this.element?.removeEventListener("loadedmetadata", this.handleTrackListChanged);
    this.element?.removeEventListener("loadeddata", this.handleTrackListChanged);
    if (this.addTrackGroupId !== void 0) {
      for (const trackElement of Array.from(this.element?.getElementsByTagName("track") ?? [])) {
        if (trackElement.dataset.groupId === this.addTrackGroupId) {
          trackElement.parentElement?.removeChild(trackElement);
        }
      }
    }
    this.element = void 0;
  }
  selectNoSubtitles(options) {
    const activeSubtitles = this.getAllTextTrackSubtitles().find((sub) => sub.track.mode !== Mode.Disabled);
    const forcedSubtitles = this.findForcedTrack(activeSubtitles?.language);
    if (this.allowAutomaticSelection) {
      logger.debug("Automatic subtitles selection disabled.");
      this.allowAutomaticSelection = false;
    }
    if (activeSubtitles?.label !== forcedSubtitles?.label) {
      if (activeSubtitles !== void 0) {
        logger.debug("Disabling:", activeSubtitles);
        activeSubtitles.track.mode = Mode.Disabled;
        this.currentActiveLabel = void 0;
      }
      if (forcedSubtitles !== void 0) {
        logger.debug("Enabling forced subtitles:", forcedSubtitles);
        forcedSubtitles.track.mode = this.activeMode;
        this.currentActiveLabel = forcedSubtitles.label;
      }
    }
    this.emitTrackChange(options?.byUser ?? true);
  }
  selectSubtitles(label, options) {
    const allSubtitles = this.getAllTextTrackSubtitles();
    const targetLabel = typeof label === "object" ? label.label : label;
    const toActivate = allSubtitles.find((sub) => sub.label === targetLabel);
    if (toActivate === void 0 || toActivate.flags.isForced) {
      return;
    }
    if (this.allowAutomaticSelection) {
      logger.debug("Automatic subtitles selection disabled.");
      this.allowAutomaticSelection = false;
    }
    const toDeactivate = allSubtitles.find((sub) => sub.track.mode !== Mode.Disabled);
    if (toActivate.label !== toDeactivate?.label) {
      if (toDeactivate !== void 0) {
        toDeactivate.track.mode = Mode.Disabled;
      }
      toActivate.track.mode = this.activeMode;
      this.currentActiveLabel = toActivate.label;
    }
    this.emitTrackChange(options?.byUser ?? true);
  }
  getActiveSubtitles() {
    const activeSubtitles = this.getAllTextTrackSubtitles().find((sub) => sub.track.mode !== Mode.Disabled);
    if (!activeSubtitles?.flags.isForced) {
      return activeSubtitles;
    }
    return void 0;
  }
  getAvailableSubtitles() {
    return this.getAllTextTrackSubtitles().filter((sub) => !sub.flags.isForced);
  }
  canAddSubtitles() {
    return true;
  }
  addSubtitles(subtitles) {
    if (this.element === void 0) {
      throw new Error("Subtitles controls are destroyed.");
    }
    if (this.addTrackGroupId === void 0) {
      this.addTrackGroupId = `sideload-${cuid.slug()}`;
    }
    const groupId = this.addTrackGroupId;
    for (const sub of subtitles) {
      const trackId = cuid.slug();
      const flags = {
        isAutoSelectable: (sub.flags?.isAutoSelectable || sub.flags?.isCaptions || sub.flags?.isDefault || sub.flags?.isForced) ?? false,
        isCaptions: sub.flags?.isCaptions ?? false,
        isDefault: sub.flags?.isDefault ?? false,
        isForced: sub.flags?.isForced ?? false
      };
      const track = document.createElement("track");
      track.kind = flags.isCaptions ? "captions" : "subtitles";
      track.label = sub.label;
      track.src = sub.src;
      track.srclang = sub.language;
      track.id = trackId;
      track.dataset.groupId = groupId;
      track.track.groupId = groupId;
      const completeSubtitles = { ...sub, flags, trackId, groupId };
      this.subtitles.push(completeSubtitles);
      this.element.appendChild(track);
    }
    return Promise.resolve();
  }
  canControlSubtitlesDisplay() {
    return true;
  }
  isSubtitlesDisplayed() {
    return this.activeMode === Mode.Showing;
  }
  displaySubtitles(display) {
    const activeMode = display ? Mode.Showing : Mode.Hidden;
    this.activeMode = activeMode;
    for (const subtitles of this.getAllTextTrackSubtitles()) {
      if (subtitles.track.mode !== Mode.Disabled) {
        subtitles.track.mode = activeMode;
      }
    }
    this.emitDisplayChange();
  }
  findForcedTrack(language) {
    const forcedSubtitles = this.getAllTextTrackSubtitles().filter((sub) => sub.flags.isForced);
    if (forcedSubtitles.length === 0) {
      return void 0;
    }
    const languages = Array.from(getClientLanguages());
    if (language !== void 0) {
      const index = languages.findIndex((lang) => lang === language);
      if (index !== -1) {
        languages.splice(index, 1);
      }
      languages.unshift(language);
    }
    const orderedSubtitles = orderByLanguage(forcedSubtitles, (sub) => sub.language, getMatchLanguages(languages));
    return orderedSubtitles[0];
  }
  /**
   * The main source of tracks and subtitles in here.
   */
  getAllTextTrackSubtitles() {
    return Array.from(this.element?.textTracks ?? []).filter((track) => (track.kind === "subtitles" || track.kind === "captions") && track.label).map((track) => this.toTextTrackSubtitles(track));
  }
  /**
   * Combine track information from the given TextTrack and this.subtitles.
   */
  toTextTrackSubtitles(track) {
    const subtitles = this.subtitles.find((sub) => {
      if (sub.label !== track.label) {
        return false;
      }
      if (this.addTrackGroupId === void 0) {
        return true;
      }
      if (sub.groupId === this.addTrackGroupId) {
        return track.groupId === this.addTrackGroupId;
      }
      return true;
    });
    if (subtitles !== void 0) {
      return {
        ...subtitles,
        track
      };
    }
    const isCaptions = track.kind === "captions";
    const isAutoSelectable = isCaptions;
    return {
      label: track.label,
      language: track.language,
      flags: { isAutoSelectable, isCaptions, isForced: false, isDefault: false },
      trackId: track.id,
      track
    };
  }
  /**
   * Process incoming track list events and make sure the expected active track
   * is enabled with the right mode or a default track is selected.
   *
   * NOTE: The 'change' event is not fired synchronously with the track mode
   * changes. The events (one per mode change in Safari 14) are fired after our
   * methods have completed ("queue a media element task [to fire an event]" in
   * the specification:
   * https://html.spec.whatwg.org/multipage/media.html#timed-text-tracks).
   */
  handleTrackListChanged(event) {
    logger.debug("Checking track list after event:", event.type);
    const allSubtitles = this.getAllTextTrackSubtitles();
    const numTracks = allSubtitles.length;
    const tracksAdded = numTracks > this.previousNumTracks;
    const allTracksPresent = numTracks >= this.subtitles.length;
    if (this.previousNumTracks !== numTracks) {
      logger.debug(`Number of subtitles tracks changed from ${this.previousNumTracks} to ${numTracks}.`);
      this.previousNumTracks = numTracks;
    }
    let firstDefaultTrack;
    let emitTrackChange = false;
    let emitCueUpdate = false;
    if (tracksAdded && allTracksPresent) {
      emitTrackChange = true;
    }
    for (const subtitles of allSubtitles) {
      if (firstDefaultTrack === void 0 && subtitles.flags.isDefault) {
        firstDefaultTrack = subtitles;
      }
      if (subtitles.track.mode !== Mode.Disabled) {
        if (this.currentActiveLabel === void 0 && this.allowAutomaticSelection) {
          this.currentActiveLabel = subtitles.label;
          emitTrackChange = true;
          logger.debug("Active label is:", this.currentActiveLabel);
          if (subtitles.track.mode !== this.activeMode) {
            logger.debug(
              `Changing active mode of ${subtitles.label} from ${subtitles.track.mode} to ${this.activeMode}.`
            );
            subtitles.track.mode = this.activeMode;
          }
        } else if (this.currentActiveLabel === subtitles.label) {
          if (subtitles.track.mode !== this.activeMode) {
            logger.debug(
              `Changing active mode of ${subtitles.label} from ${subtitles.track.mode} to ${this.activeMode}.`
            );
            subtitles.track.mode = this.activeMode;
          }
        } else {
          logger.debug(`Got unexpected active track. Disabling ${subtitles.label}`);
          subtitles.track.mode = Mode.Disabled;
        }
      }
      if (subtitles.track.mode === Mode.Disabled) {
        if (subtitles.track.oncuechange !== null) {
          logger.debug("Removing cue event listener from:", subtitles.label);
          subtitles.track.oncuechange = null;
          emitCueUpdate = true;
        }
      } else {
        if (subtitles.track.oncuechange === null) {
          logger.debug("Adding cue event listener to:", subtitles.label);
          subtitles.track.oncuechange = this.emitCueUpdate.bind(this, subtitles.track);
          emitCueUpdate = true;
        }
      }
    }
    if (tracksAdded && this.currentActiveLabel === void 0 && firstDefaultTrack !== void 0 && this.allowAutomaticSelection) {
      logger.debug("Enabling default subtitles:", firstDefaultTrack);
      firstDefaultTrack.track.mode = this.activeMode;
      this.currentActiveLabel = firstDefaultTrack.label;
      emitTrackChange = true;
      logger.debug("Adding cue event listener to:", firstDefaultTrack.label);
      firstDefaultTrack.track.oncuechange = this.emitCueUpdate.bind(this, firstDefaultTrack.track);
      emitCueUpdate = true;
    }
    if (emitTrackChange) {
      this.emitTrackChange(false);
    }
    if (emitCueUpdate) {
      this.emitCueUpdate();
    }
  }
  emitTrackChange(byUser) {
    this.eventEmitter.emit("playercore.subtitleschange" /* SubtitlesChange */, {
      byUser,
      active: this.getActiveSubtitles()
    });
  }
  emitDisplayChange() {
    this.eventEmitter.emit("playercore.subtitlesdisplaychange" /* SubtitlesDisplayChange */, {
      displayed: this.isSubtitlesDisplayed()
    });
  }
  emitCueUpdate(track) {
    this.eventEmitter.emit("playercore.subtitlescueupdate" /* SubtitlesCueUpdate */, {
      activeCues: Array.from(track?.activeCues ?? [])
    });
  }
};

export {
  HTMLMediaSubtitles
};
